import { useMemo, useState } from "react";
import { exposureThemes, exposures } from "@/mocks/homeExposures";

interface GallerySectionProps {
  onOpen: (title: string) => void;
}

export default function GallerySection({ onOpen }: GallerySectionProps) {
  const [activeTheme, setActiveTheme] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    const kw = keyword.trim();
    return exposures.filter((item) => {
      const matchTheme = activeTheme === "全部" || item.theme === activeTheme;
      const matchKeyword =
        kw === "" ||
        item.title.includes(kw) ||
        item.excerpt.includes(kw) ||
        item.source.includes(kw);
      return matchTheme && matchKeyword;
    });
  }, [activeTheme, keyword]);

  return (
    <section id="gallery" className="w-full border-t border-background-200 bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              暗网截图大图库 · 大图卡片流
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
              封面驱动的爆料卡片，先看画面再点进去核对来源。切换主题或检索关键词，能更快锁定要看的暗网截图与线索。
            </p>
          </div>

          <label className="relative flex w-full items-center lg:w-72">
            <span className="pointer-events-none absolute left-3 flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="检索标题 / 来源，如：镜像"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
            />
          </label>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {exposureThemes.map((theme) => (
            <button
              key={theme}
              type="button"
              onClick={() => setActiveTheme(theme)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${
                activeTheme === theme
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {theme}
            </button>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {list.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
              >
                <div className="relative h-52 w-full overflow-hidden bg-background-100">
                  <img
                    src={item.cover}
                    alt={`黑网爆料 ${item.theme} ${item.title}`}
                    title={`${item.title} 暗网截图 暗网线索`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-3 py-1 text-xs text-secondary-900">
                    {item.theme}
                  </span>
                  {item.badge && (
                    <span className="absolute right-3 top-3 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-foreground-950">
                      {item.badge}
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-base font-semibold leading-snug text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {item.excerpt}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-archive-line"></i>
                      {item.source}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {item.date}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {item.reads}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpen(item.title)}
                    className="mt-4 inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-md bg-secondary-100 px-3.5 py-2 text-sm text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                  >
                    查看片段
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-700">没有匹配的条目，换个关键词试试。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setActiveTheme("全部");
              }}
              className="mt-4 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}